import { apiUrl } from "./apiUrl";
import { HttpError } from "./httpError";
import { IResponse } from "../../types/response.type";

type QueryValue = string | number | boolean | null | undefined;

export type ApiRequestOptions = Omit<RequestInit, "body"> & {
	path: string;
	query?: Record<string, QueryValue>;
	json?: unknown;
};

async function readJsonSafely(response: Response): Promise<unknown> {
	const contentType = response.headers.get("content-type") || "";
	if (!contentType.includes("application/json")) return undefined;
	try {
		return await response.json();
	} catch {
		return undefined;
	}
}

export async function apiRequestRaw<T>(options: ApiRequestOptions): Promise<T> {
	const { path, query, json, headers, credentials, ...init } = options;
	const url = `${apiUrl}${path}`;

	const hasJsonBody = json !== undefined;
	const requestHeaders: HeadersInit = {
		...(hasJsonBody ? { "Content-Type": "application/json" } : {}),
		...(headers || {}),
	};

	const response = await fetch(url, {
		...init,
		headers: requestHeaders,
		credentials: credentials ?? "include",
		body: hasJsonBody ? JSON.stringify(json) : undefined,
	});

	if (!response.ok) {
		const payload = await readJsonSafely(response);
		throw new HttpError(
			`Request failed: ${response.status} ${response.statusText}`,
			response.status,
			payload,
		);
	}

	// 204 No Content
	if (response.status === 204) {
		return undefined as T;
	}

	const payload = await readJsonSafely(response);
	return payload as T;
}

function extractErrorMessage(payload: unknown): string | undefined {
	if (!payload || typeof payload !== "object") return undefined;
	const maybe = payload as Record<string, unknown>;
	const detail = maybe["detail"];
	if (typeof detail === "string") return detail;
	const message = maybe["message"];
	if (typeof message === "string") return message;
	return undefined;
}

export async function apiRequest<T>(
	options: ApiRequestOptions,
): Promise<IResponse<T>> {
	try {
		const data = await apiRequestRaw<T>(options);
		return { success: true, data };
	} catch (e) {
		if (e instanceof HttpError) {
			return {
				success: false,
				message: extractErrorMessage(e.payload) || e.message,
				data: null,
			};
		}
		return {
			success: false,
			message: e instanceof Error ? e.message : "Unknown error",
			data: null,
		};
	}
}
